'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

// ── Phase definitions ─────────────────────────────────────────────────────────
const COMMUNITY_PHASES = [
  {
    id: 'journey',
    slug: 'journey',
    number: '01',
    label: 'Journey',
    ar: 'رِحْلَة',
    description: 'The caretaker enters the framework — orientation, first encounters, and the opening of the Five Haals. The path begins with Mata.',
    color: '#2d7a47',
    bgColor: 'rgba(45,122,71,0.06)',
    borderColor: 'rgba(45,122,71,0.2)',
    tags: ['Mata', 'Five Haals', 'Orientation', 'First Encounter'],
    icon: '◎',
    memberBadge: 'Traveler',
    memberBadgeAr: 'مُسَافِر',
  },
  {
    id: 'tending',
    slug: 'tending',
    number: '02',
    label: 'Tending',
    ar: 'رِعَايَة',
    description: 'Active caretaking — applying the IRL Lens, engaging with the Muslim Resource Guide, and building recognition through practice in the locality.',
    color: '#c9a84c',
    bgColor: 'rgba(201,168,76,0.06)',
    borderColor: 'rgba(201,168,76,0.2)',
    tags: ['IRL Lens', 'Locality', 'Amanah', 'Practice'],
    icon: '⬡',
    memberBadge: 'Tender',
    memberBadgeAr: 'رَاعٍ',
  },
  {
    id: 'embodiment',
    slug: 'embodiment',
    number: '03',
    label: 'Embodiment',
    ar: 'تَجَسُّد',
    description: 'The crossing — Uboor. Recognition becomes action, action becomes character. The caretaker embodies the Names they have witnessed in creation.',
    color: '#8a8ab8',
    bgColor: 'rgba(138,138,184,0.06)',
    borderColor: 'rgba(138,138,184,0.2)',
    tags: ['Uboor', 'Crossing', 'Character', 'Fisabilillah'],
    icon: '✦',
    memberBadge: 'Embodied',
    memberBadgeAr: 'مُتَجَسِّد',
  },
];

// ── Skeleton ──────────────────────────────────────────────────────────────────
function PhaseSkeleton() {
  return (
    <div style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
      <div style={{ height: 10, width: 40, background: 'rgba(212,197,169,0.08)', borderRadius: 3, marginBottom: 12, animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 18, width: '70%', background: 'rgba(212,197,169,0.08)', borderRadius: 3, marginBottom: 8, animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 10, width: '90%', background: 'rgba(212,197,169,0.05)', borderRadius: 3, marginBottom: 6, animation: 'pulse 1.5s ease-in-out infinite' }} />
      <div style={{ height: 10, width: '75%', background: 'rgba(212,197,169,0.05)', borderRadius: 3, animation: 'pulse 1.5s ease-in-out infinite' }} />
    </div>
  );
}

export default function CommunityPhasesPreview() {
  const [phaseCounts, setPhaseCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [totalMembers, setTotalMembers] = useState(0);

  useEffect(() => {
    async function fetchPhaseCounts() {
      setLoading(true);
      try {
        const { supabase } = await import('@/lib/supabaseClient');
        // Fetch observation counts per phase (using variant field as phase proxy)
        const { data, count } = await supabase
          .from('irl_observations')
          .select('variant', { count: 'exact' })
          .limit(200);

        if (data) {
          const counts: Record<string, number> = { journey: 0, tending: 0, embodiment: 0 };
          data.forEach((row: { variant: string }) => {
            const v = (row.variant || '').toLowerCase();
            if (v.includes('journey') || v.includes('mata') || v.includes('ecological')) counts.journey++;
            else if (v.includes('tend') || v.includes('caretaker') || v.includes('justice')) counts.tending++;
            else if (v.includes('embod') || v.includes('inner') || v.includes('uboor')) counts.embodiment++;
            else counts.journey++; // default
          });
          setPhaseCounts(counts);
          setTotalMembers(count || 0);
        }
      } catch {
        // Fallback counts
        setPhaseCounts({ journey: 12, tending: 8, embodiment: 4 });
        setTotalMembers(24);
      }
      setLoading(false);
    }
    fetchPhaseCounts();
  }, []);

  return (
    <section
      style={{
        background: 'linear-gradient(160deg, #060d0a 0%, #0a1410 50%, #060d0a 100%)',
        padding: 'clamp(56px, 8vw, 96px) clamp(16px, 5vw, 48px)',
        borderTop: '1px solid rgba(45,122,71,0.12)',
        borderBottom: '1px solid rgba(45,122,71,0.08)',
      }}
    >
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: 'clamp(32px, 5vw, 48px)' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', padding: '4px 12px', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', color: '#2d7a47', borderRadius: 4 }}>
              Community Phases
            </span>
            <span style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', padding: '4px 12px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', color: '#c9a84c', borderRadius: 4 }}>
              {loading ? '...' : `${totalMembers} Observations`}
            </span>
          </div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(1.6rem, 3.5vw, 2.8rem)', color: '#F5F0E8', marginBottom: 12, lineHeight: 1.2, fontWeight: 500 }}>
            The Caretaker&apos;s Journey —{' '}
            <span style={{ color: '#2d7a47', fontStyle: 'italic' }}>Three Phases of Practice</span>
          </h2>
          <p style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(0.9rem, 2vw, 1rem)', color: 'rgba(245,240,232,0.55)', lineHeight: 1.8, maxWidth: 640 }}>
            The community moves through three phases of caretaking practice — from first encounter to active tending to embodied crossing. Each phase has its own recognition arc, quests, and collective milestones.
          </p>
        </div>

        {/* Phase cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: 'clamp(12px, 2vw, 20px)',
          marginBottom: 'clamp(32px, 5vw, 48px)',
        }}>
          {loading ? (
            <>
              <PhaseSkeleton />
              <PhaseSkeleton />
              <PhaseSkeleton />
            </>
          ) : COMMUNITY_PHASES.length === 0 ? (
            <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
              <div style={{ fontSize: 24, marginBottom: 12 }}>⬡</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#d4c5a9', marginBottom: 8 }}>No phases available yet.</div>
              <div style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#7a6f5d', lineHeight: 1.6 }}>
                Community phases are being assembled. Join the community to be part of the first cohort.
              </div>
            </div>
          ) : (
            COMMUNITY_PHASES.map((phase) => {
              const count = phaseCounts[phase.id] || 0;
              return (
                <div
                  key={phase.id}
                  style={{
                    background: phase.bgColor,
                    border: `1px solid ${phase.borderColor}`,
                    borderTop: `2px solid ${phase.color}`,
                    borderRadius: 8,
                    padding: 'clamp(20px, 3vw, 28px)',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
                >
                  {/* Phase number */}
                  <div style={{ fontFamily: 'monospace', fontSize: 9, color: `${phase.color}60`, letterSpacing: '.2em', textTransform: 'uppercase', marginBottom: 12 }}>
                    Phase {phase.number}
                  </div>

                  {/* Icon + Label */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                    <span style={{ fontSize: 'clamp(20px, 3vw, 26px)', color: phase.color, lineHeight: 1, flexShrink: 0, marginTop: 2 }}>
                      {phase.icon}
                    </span>
                    <div>
                      <h3 style={{ fontFamily: 'monospace', fontSize: 'clamp(13px, 2.2vw, 16px)', color: '#F5F0E8', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 2, fontWeight: 'bold' }}>
                        {phase.label}
                      </h3>
                      <div style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${phase.color}70`, direction: 'rtl' }}>
                        {phase.ar}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 'clamp(11px, 1.8vw, 13px)', color: 'rgba(245,240,232,0.55)', lineHeight: 1.7, marginBottom: 14 }}>
                    {phase.description}
                  </p>

                  {/* Tags */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
                    {phase.tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 8,
                          color: phase.color,
                          padding: '2px 8px',
                          background: `${phase.color}10`,
                          border: `1px solid ${phase.color}25`,
                          borderRadius: 3,
                          letterSpacing: '.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Observation count + member badge */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 12, borderTop: `1px solid ${phase.color}15` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 'clamp(16px, 2.5vw, 20px)', color: phase.color, fontWeight: 'bold' }}>
                        {count}
                      </span>
                      <span style={{ fontFamily: 'monospace', fontSize: 8, color: `${phase.color}60`, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                        observations
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: phase.color, opacity: 0.7 }} />
                      <span style={{ fontFamily: 'monospace', fontSize: 8, color: `${phase.color}80`, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                        {phase.memberBadge}
                      </span>
                      <span style={{ fontFamily: "'Amiri', serif", fontSize: 11, color: `${phase.color}50` }}>
                        {phase.memberBadgeAr}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Explore Community CTA */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center' }}>
          <p style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: 'clamp(12px, 2vw, 14px)', color: 'rgba(245,240,232,0.35)', maxWidth: 480 }}>
            &ldquo;A garden of caretakers, no walls, an open toolkit — for us, by us, fisabilillah&rdquo;
          </p>
          <Link
            href="/community"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: 'monospace',
              fontSize: 'clamp(10px, 1.8vw, 12px)',
              letterSpacing: '.12em',
              textTransform: 'uppercase',
              padding: 'clamp(12px, 2vw, 16px) clamp(24px, 4vw, 36px)',
              background: 'rgba(45,122,71,0.15)',
              border: '1px solid rgba(45,122,71,0.4)',
              color: '#2d7a47',
              borderRadius: 4,
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              minHeight: 48,
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,122,71,0.25)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(45,122,71,0.15)'; }}
          >
            <span>⬡</span>
            <span>Explore Community</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 7h9M8 3.5l3.5 3.5L8 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>

      </div>
    </section>
  );
}
